import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { getTopicDetail } from '@/features/topics/api/topicsClient'
import { fetchPosts } from '@/features/posts/api/postsClient'
import type { TopicTrendData, DayBucket, SourceEntry } from '../model/contracts'

function isoDate(dt: DateTime): string {
  return dt.toISODate() ?? dt.toFormat('yyyy-MM-dd')
}

function deriveBuckets(
  items: { timestamp: string; platform: string; author?: { handle?: string } }[],
  startIso: string,
  endIso: string,
): DayBucket[] {
  const start = DateTime.fromISO(startIso)
  const end = DateTime.fromISO(endIso)
  const totalDays = Math.round(end.diff(start, 'days').days) + 1

  const map = new Map<string, DayBucket>()
  for (let i = 0; i < totalDays; i++) {
    const d = isoDate(start.plus({ days: i }))
    map.set(d, { date: d, posts: 0, news: 0 })
  }

  for (const item of items) {
    const d = isoDate(DateTime.fromISO(item.timestamp))
    const bucket = map.get(d)
    if (!bucket) continue
    const p = item.platform.toLowerCase()
    if (p === 'facebook' || p === 'x' || p === 'twitter' || p === 'threads') {
      bucket.posts += 1
    } else {
      bucket.news += 1
    }
  }

  return [...map.values()]
}

function deriveSources(
  items: { platform: string; author: { name: string; handle?: string } }[],
): SourceEntry[] {
  const map = new Map<string, SourceEntry>()
  for (const item of items) {
    const p = item.platform.toLowerCase()
    const isSocial = p === 'facebook' || p === 'x' || p === 'twitter' || p === 'threads'
    const domain = item.author.handle ?? item.author.name
    const key = domain
    const existing = map.get(key)
    if (existing) {
      existing.postCount += 1
    } else {
      map.set(key, { domain, postCount: 1, kind: isSocial ? 'fb' : 'news' })
    }
  }
  return [...map.values()].sort((a, b) => b.postCount - a.postCount)
}

export function useTopicTrendData(topicId: string): {
  data: TopicTrendData | undefined
  isLoading: boolean
  error: unknown
} {
  const endDate = DateTime.now()
  const startDate = endDate.minus({ days: 29 })
  const startIso = isoDate(startDate)
  const endIso = isoDate(endDate)

  const topicQuery = useQuery({
    queryKey: ['topics', topicId, 'detail'],
    queryFn: ({ signal }) => getTopicDetail(topicId, signal),
    staleTime: 300_000,
    enabled: Boolean(topicId),
  })

  const keywords = topicQuery.data?.keywords ?? []
  const topicName = topicQuery.data?.name ?? ''

  const postsQuery = useQuery({
    queryKey: ['topics-trend', topicId, 'posts', startIso, endIso],
    queryFn: ({ signal }) =>
      fetchPosts(
        {
          keywords: keywords.length > 0 ? keywords : undefined,
          startDate: startIso,
          endDate: endIso,
          limit: 500,
          page: 1,
        },
        signal,
      ),
    staleTime: 5 * 60 * 1000,
    enabled: Boolean(topicId) && !topicQuery.isLoading,
  })

  const data = useMemo<TopicTrendData | undefined>(() => {
    if (!postsQuery.data) return undefined
    const items = postsQuery.data.items
    return {
      topicName,
      days: deriveBuckets(items, startIso, endIso),
      sources: deriveSources(items),
    }
  }, [postsQuery.data, topicName, startIso, endIso])

  return {
    data,
    isLoading: topicQuery.isLoading || postsQuery.isLoading,
    error: topicQuery.error ?? postsQuery.error,
  }
}
